<template>
  <div ref="modalRoot" class="modal fade register-product-modal" :class="{ show: showModal }"
       :style="{ display: showModal ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">

        <!-- header -->
        <div class="modal-header">
          <div class="modal-title-wrap">
            <p class="modal-eyebrow">Gestión de productos</p>
            <h5 class="modal-title">{{ title }}</h5>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <!-- body -->
        <div class="modal-body">
          <div v-if="saveFeedback.message" class="save-feedback" :class="`save-feedback--${saveFeedback.type}`" role="status" aria-live="polite">
            {{ saveFeedback.message }}
          </div>

          <div class="mobile-section-toggle">
            <button type="button" class="btn btn-section" :class="{ active: activeSection === 'basico' }" @click="activeSection = 'basico'">
              Básico
            </button>
            <button type="button" class="btn btn-section" :class="{ active: activeSection === 'avanzado' }" @click="activeSection = 'avanzado'">
              Avanzado
            </button>
          </div>

          <div class="form-wrapper">
            <div class="product-quick-summary">
              <div class="summary-item">
                <span class="summary-label">Código de barras</span>
                <strong>{{ productData.codigo_barras || 'Sin código de barras' }}</strong>
              </div>
              <div class="summary-item">
                <span class="summary-label">Código interno</span>
                <strong>{{ resolvedInternalCode || 'Se generará automáticamente' }}</strong>
              </div>
              <div class="summary-item">
                <span class="summary-label">Modo</span>
                <strong>{{ productData.es_granel ? 'A granel' : 'Producto normal' }}</strong>
              </div>
            </div>

            <div v-if="readOnly && productData.descripcion" class="product-description-summary">
              <span class="summary-label">Descripción</span>
              <p>{{ productData.descripcion }}</p>
            </div>

            <div class="section-block" :class="{ 'section-hidden-mobile': activeSection !== 'basico' }">
              <div class="section-heading">
                <h6 class="section-title">Datos básicos</h6>
                <p class="section-copy">Alta rápida pensada para celular o tablet.</p>
              </div>
              <div class="form-grid">
              <div class="form-field" data-field="codigo_barras">
                <label class="form-label" for="codigo_barras">Código de Barras</label>
                <input
                  id="codigo_barras"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.codigo_barras }"
                  v-model.trim="productData.codigo_barras"
                  placeholder="Código de Barras"
                  inputmode="numeric"
                  maxlength="13"
                  :disabled="barcodeLookupLoading || readOnly"
                >
                <small v-if="!barcodeLookupLoading && !barcodeLookupMessage" class="field-hint">Opcional. Si no existe, el backend generará un código interno.</small>
                <small v-if="barcodeLookupLoading" class="field-hint">Buscando datos del producto...</small>
                <small v-else-if="barcodeLookupMessage" class="field-hint">{{ barcodeLookupMessage }}</small>
                <small v-if="errors.codigo_barras" class="field-error">{{ errors.codigo_barras }}</small>
              </div>
              <div class="form-field" data-field="nombre">
                <label class="form-label" for="nombre">Nombre</label>
                <input id="nombre" type="text" class="form-control" :class="{ 'is-invalid': errors.nombre }" v-model="productData.nombre" placeholder="Nombre del Producto" :disabled="readOnly">
                <small v-if="errors.nombre" class="field-error">{{ errors.nombre }}</small>
              </div>
              <div class="form-field" data-field="categoria_id">
                <label class="form-label">Categoría</label>
                <div class="category-field-row">
                  <CategorySelect
                    ref="categorySelect"
                    v-model="productData.categoria_id"
                    :disabled="isSaving || readOnly"
                  />
                  <button
                    v-if="puedeCrearCategoria && !readOnly"
                    type="button"
                    class="btn btn-outline-success btn-sm category-add-btn"
                    title="Crear categoría"
                    :disabled="isSaving"
                    @click="showCategoriaRapida = true"
                  >
                    +
                  </button>
                </div>
                <small v-if="errors.categoria_id" class="field-error">{{ errors.categoria_id }}</small>
              </div>
              <div class="form-field" data-field="unidad_medida_id">
                <label class="form-label">Unidad de Medida</label>
                <MeasureUnitSelect
                  v-model="productData.unidad_medida_id"
                  :disabled="isSaving || readOnly"
                />
                <small v-if="errors.unidad_medida_id" class="field-error">{{ errors.unidad_medida_id }}</small>
              </div>
              <div class="form-field" data-field="tipo_iva">
                <label class="form-label" for="tipo_iva">Tipo IVA</label>
                <select id="tipo_iva" class="form-select" v-model.number="productData.tipo_iva" :disabled="readOnly">
                  <option :value="1">IVA 10%</option>
                  <option :value="2">IVA 5%</option>
                  <option :value="3">Exenta</option>
                </select>
              </div>
            </div>
              </div>

            <div class="section-block" :class="{ 'section-hidden-mobile': activeSection !== 'avanzado' }">
              <div class="section-heading">
                <h6 class="section-title">Datos avanzados</h6>
                <p class="section-copy">Campos opcionales y ajustes operativos.</p>
              </div>
              <div class="form-grid">
              <div class="form-field" data-field="descripcion">
                <label class="form-label" for="descripcion">Descripción</label>
                <input id="descripcion" type="text" class="form-control" v-model="productData.descripcion" placeholder="Descripción" :disabled="readOnly">
              </div>
              <div class="form-field" data-field="codigo_producto">
                <label class="form-label" for="codigo_producto">Código interno</label>
                <input id="codigo_producto" type="text" class="form-control" :class="{ 'is-invalid': errors.codigo_producto }" v-model.trim="productData.codigo_producto" placeholder="Código interno del producto" maxlength="30" :disabled="readOnly">
                <small v-if="showInternalCodeFallbackHint" class="field-hint">Si cargas código de barras, ese mismo valor puede usarse como código interno.</small>
                <small v-else class="field-hint">Opcional. Si lo dejas vacío, el backend generará un SKU interno.</small>
                <small v-if="errors.codigo_producto" class="field-error">{{ errors.codigo_producto }}</small>
              </div>
              <div class="form-field" data-field="url_imagen">
                <label class="form-label" for="url_imagen">URL de Imagen</label>
                <input id="url_imagen" type="text" class="form-control" v-model="productData.url_imagen" placeholder="imagen.jpg" :disabled="readOnly">
              </div>
              <div v-if="!readOnly" class="form-field product-image-upload" data-field="imagen_producto">
                <label class="form-label">Foto del producto</label>
                <div v-if="productData.url_imagen || pendingImagePreviewUrl" class="product-image-preview">
                  <img :src="pendingImagePreviewUrl || productData.url_imagen" alt="Imagen del producto">
                </div>
                <button
                  v-if="(isEditMode && productData.url_imagen) || pendingImagePreviewUrl"
                  type="button"
                  class="btn btn-outline-secondary btn-sm mb-2"
                  @click="openCropModalForCurrentImage"
                >
                  Recortar imagen actual
                </button>
                <div class="product-image-actions">
                  <label class="btn btn-outline-secondary btn-sm mb-0">
                    Subir imagen
                    <input type="file" accept="image/*" class="visually-hidden" :disabled="imageUploadLoading" @change="handleImageFile">
                  </label>
                  <label class="btn btn-outline-secondary btn-sm mb-0">
                    Tomar foto
                    <input type="file" accept="image/*" capture="environment" class="visually-hidden" :disabled="imageUploadLoading" @change="handleImageFile">
                  </label>
                  <label class="btn btn-outline-secondary btn-sm mb-0">
                    Recortar manualmente
                    <input type="file" accept="image/*" class="visually-hidden" :disabled="imageUploadLoading" @change="handleImageFileForManualCrop">
                  </label>
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="!isEditMode || iaSearchLoading || imageUploadLoading"
                    @click="searchImageWithIA"
                  >
                    {{ iaSearchLoading ? 'Buscando…' : 'Buscar con IA' }}
                  </button>
                  <button
                    v-if="isEditMode && productData.url_imagen"
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="cleanBackgroundLoading || imageUploadLoading"
                    @click="cleanImageBackground"
                  >
                    {{ cleanBackgroundLoading ? 'Limpiando…' : 'Limpiar fondo' }}
                  </button>
                </div>
                <small v-if="!isEditMode" class="field-hint">La búsqueda con IA se habilita después de guardar el producto.</small>
                <small v-if="!isEditMode" class="field-hint">El fondo se limpia automáticamente al guardar el producto; si hace falta reintentarlo a mano, esa opción también se habilita después de guardar.</small>
                <small v-if="imageUploadLoading" class="field-hint">Subiendo imagen...</small>
                <small v-else-if="pendingImageDataUrl && !isEditMode" class="field-hint">Se subirá al guardar el producto.</small>
                <small v-if="imageUploadError" class="field-error">{{ imageUploadError }}</small>
                <small v-if="cleanBackgroundError" class="field-error">{{ cleanBackgroundError }}</small>
                <div v-if="iaCandidates" class="ia-candidates">
                  <p v-if="!iaCandidates.length" class="field-hint mb-0">No se encontraron imágenes candidatas para este producto.</p>
                  <template v-else>
                    <small class="field-hint d-block">
                      Elegí una imagen candidata{{ productData.url_imagen ? ' — reemplazará la imagen actual' : '' }}:
                    </small>
                    <div class="ia-candidates-grid">
                      <div
                        v-for="candidata in iaCandidates"
                        :key="candidata.url"
                        class="ia-candidate"
                        :class="{ 'ia-candidate--applying': iaApplyingUrl === candidata.url }"
                      >
                        <button
                          type="button"
                          class="ia-candidate-apply"
                          :disabled="!!iaApplyingUrl"
                          @click="applyIACandidate(candidata)"
                        >
                          <img :src="candidata.url" alt="Imagen candidata">
                          <span class="ia-candidate-fuente">{{ candidata.fuente }}</span>
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-secondary btn-sm ia-candidate-crop"
                          :disabled="!!iaApplyingUrl || cropCandidateLoading === candidata.url"
                          @click="openCropModalForCandidate(candidata)"
                        >
                          {{ cropCandidateLoading === candidata.url ? 'Cargando…' : 'Recortar' }}
                        </button>
                      </div>
                    </div>
                  </template>
                </div>
                <small v-if="iaSearchError" class="field-error">{{ iaSearchError }}</small>
              </div>
              <div class="form-field switch-field" data-field="activo">
                <label class="form-label" for="activo">Estado</label>
                <div class="form-switcher">
                  <input class="form-check-input" type="checkbox" id="activo" v-model="productData.activo" :disabled="readOnly">
                  <label class="form-check-label" for="activo">Activo</label>
                </div>
              </div>
              <div class="form-field switch-field" data-field="es_granel">
                <label class="form-label" for="es_granel">Producto a granel</label>
                <div class="form-switcher">
                  <input class="form-check-input" type="checkbox" id="es_granel" v-model="productData.es_granel" :disabled="readOnly">
                  <label class="form-check-label" for="es_granel">Sí</label>
                </div>
              </div>
              <div class="form-field" v-if="productData.es_granel" data-field="codigo_plu_balanza">
                <label class="form-label" for="codigo_plu_balanza">Código PLU balanza</label>
                <input
                  id="codigo_plu_balanza"
                  type="text"
                  class="form-control"
                  v-model.trim="productData.codigo_plu_balanza"
                  placeholder="PLU único del producto"
                  :disabled="readOnly"
                >
                <small class="field-hint">Opcional. Si el producto es a granel y lo dejas vacío, el backend generará un PLU.</small>
                <small v-if="errors.codigo_plu_balanza" class="field-error">{{ errors.codigo_plu_balanza }}</small>
              </div>
            </div>
            </div>
          </div>
        </div>

        <!-- footer -->
        <div class="modal-footer">
          <div class="modal-hint">
            {{ modalHint }}
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" :disabled="isSaving || deleting" @click="closeModal">{{ readOnly ? 'Cerrar' : 'Cancelar' }}</button>
            <button
              v-if="allowDelete"
              type="button"
              class="btn btn-outline-danger"
              :disabled="isSaving || deleting"
              @click="$emit('delete-product')"
            >
              {{ deleting ? 'Revirtiendo...' : 'Revertir carga' }}
            </button>
            <button v-if="showSaveAndResetAction" type="button" class="btn btn-outline-primary" :disabled="isSaving" @click="handleSaveAndReset">Guardar y cargar otro</button>
            <button v-if="!readOnly" type="button" class="btn btn-primary" :disabled="isSaving" @click="handleSave">
              {{ isSaving ? 'Guardando...' : 'Guardar Producto' }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
  <div v-if="showModal" class="modal-backdrop fade show register-product-backdrop"></div>
  <ImageCropModal
    v-if="showCropModal"
    :image-src="cropImageSrc"
    @confirm="handleCropConfirmed"
    @cancel="handleCropCancelled"
  />

  <!-- RF-5: alta rápida de categoría sin abandonar el alta de producto. Se
       apila sobre este modal; cancelarla deja intacto todo lo ya tipeado.
       El envoltorio no es decorativo: `CategoriaRapidaModal` renderiza un
       fragmento (modal + backdrop), así que una clase suelta no cae por
       fallthrough. Es el ancla de la elevación de capas, ver
       `.stacked-modal-layer` en los estilos. -->
  <div class="stacked-modal-layer">
    <CategoriaRapidaModal
      v-model:showModal="showCategoriaRapida"
      @categoria-creada="onCategoriaCreada"
    />
  </div>
</template>

<script>
import apiService from '../services/apiService.js';
import CategorySelect from './CategorySelect.vue';
import MeasureUnitSelect from './MeasureUnitSelect.vue';
import ImageCropModal from './ImageCropModal.vue';
import CategoriaRapidaModal from './CategoriaRapidaModal.vue';
import { hasPermission } from '../services/authSessionService';


export default {
  name: 'RegisterProductModal',
  components: { CategorySelect, MeasureUnitSelect, ImageCropModal, CategoriaRapidaModal },
  emits:['update:showModal','product-registered','delete-product'],
  props: {
    showModal: { type: Boolean, default: false },
    title: { type: String, default: 'Registrar Producto' },
    initialCode: { type: String, default: '' },
    // Alta desde una línea de factura de compra (Compra desde foto): nombre y
    // tipo de IVA leídos. Sin ellos, el alta es la de siempre.
    initialNombre: { type: String, default: '' },
    initialTipoIva: { type: Number, default: null },
    product: { type:Object, default:null },
    readOnly: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    deleting: { type: Boolean, default: false }
  },
  data() {
    return {
      activeSection: 'basico',
      isMobileViewport: false,
      isSaving: false,
      showCategoriaRapida: false,
      barcodeLookupLoading: false,
      barcodeLookupMessage: '',
      barcodeLookupTimeoutId: null,
      lastAutofillName: '',
      // Foto del producto (RF-6 de SPEC_BACKFILL_IMAGENES_CATALOGO_v0.1.md,
      // extiende EPI RF-1): en edición se sube apenas se elige el archivo; en
      // alta no hay producto_id todavía, se guarda pendiente y se sube recién
      // después de crear el producto (ver saveProduct()).
      pendingImageDataUrl: null,
      pendingImagePreviewUrl: '',
      pendingImageOmitirLimpieza: false,
      imageUploadLoading: false,
      imageUploadError: '',
      // Recorte manual (pedido del usuario 2026-08-02): permite recortar a
      // mano una imagen nueva, la ya guardada, o una candidata de IA, sin
      // pasar por el procesamiento automático de fondo/encuadre.
      showCropModal: false,
      cropImageSrc: '',
      cropSourceObjectUrl: null,
      cropCandidateLoading: '',
      // Búsqueda IA por producto (BIC-7B-001, completa BIC-5A-005): a
      // diferencia del backfill masivo, acá el operador ve candidatas y
      // elige — la elección es la confirmación (BIC-7V-02).
      iaSearchLoading: false,
      iaSearchError: '',
      iaCandidates: null,
      iaApplyingUrl: '',
      // Limpieza de fondo on-demand sobre la imagen ya guardada (hallazgo
      // de verificación manual 2026-08-01): una foto existente no tenía
      // forma de re-limpiarse sin resubir el archivo.
      cleanBackgroundLoading: false,
      cleanBackgroundError: '',
      saveFeedback: {
        type: 'info',
        message: ''
      },
      errors: {},
      productData: {
        codigo_barras: this.initialCode,
        codigo_producto: '',
        codigo_plu_balanza: '',
        es_granel: false,
        nombre: this.initialNombre,
        descripcion: '',
        url_imagen: '',
        categoria_id: null,
        unidad_medida_id: null,
        activo: true,
        tipo_iva: this.initialTipoIva || 1
      }
    };
  },
  computed: {
    // El botón sólo se muestra a quien puede usarlo: POST /api/categories exige
    // `crear_categoria` (categoryRoutes.js). Ofrecer una acción que el backend
    // va a rechazar con 403 es lo que FE-SDD-131 dejó prohibido.
    puedeCrearCategoria() {
      return hasPermission('crear_categoria');
    },
    isEditMode() {
      return Boolean(this.productData.producto_id);
    },
    resolvedInternalCode() {
      return this.getEffectiveInternalCode();
    },
    showInternalCodeFallbackHint() {
      return Boolean(this.defaultInternalCode());
    },
    showSaveAndResetAction() {
      return !this.readOnly && !this.isEditMode && !this.isMobileViewport;
    },
    modalHint() {
      if (this.readOnly) {
        return 'Resultado de la importación asistida. Revisa los datos resueltos por el agente.';
      }
      if (this.allowDelete && this.isEditMode) {
        return 'Producto importado. Puedes ajustar los datos en esta misma pantalla o revertir la carga si corresponde.';
      }
      if (this.isSaving) {
        return 'Guardando producto y validando datos...';
      }
      if (this.saveFeedback.type === 'danger' && this.saveFeedback.message) {
        return this.saveFeedback.message;
      }
      if (Object.keys(this.errors).length > 0) {
        return 'Corrige los campos marcados antes de guardar.';
      }
      if (this.isMobileViewport) {
        return 'Completa los datos básicos y guarda. Los campos avanzados son opcionales salvo reglas operativas.';
      }
      return 'Revisa que los datos sean correctos antes de guardar.';
    }
  },
  watch: {
    'productData.codigo_barras'(newCode, oldCode) {
      if (this.readOnly) return;
      if (!this.showModal || !newCode || newCode === oldCode) return;
      this.syncDefaultInternalCode();
      if (this.barcodeLookupTimeoutId) clearTimeout(this.barcodeLookupTimeoutId);
      this.barcodeLookupTimeoutId = setTimeout(() => {
        this.lookupBarcodeMetadata();
      }, 350);
    },
    'productData.codigo_producto'() {
      if (this.errors.codigo_producto && String(this.productData.codigo_producto || '').trim()) {
        this.errors = {
          ...this.errors,
          codigo_producto: ''
        };
        if (!this.errors.codigo_producto) {
          delete this.errors.codigo_producto;
        }
      }
    },
    initialCode(newCode) {
      if (this.showModal) {
        this.productData.codigo_barras = newCode;
        this.syncDefaultInternalCode();
        console.log('[RegisterProductModal] initialCode cambió a', newCode);
      }
    },
    'productData.es_granel'(isBulk) {
      if (this.readOnly) return;
      if (!isBulk && !this.isEditMode) {
        this.productData.codigo_producto = this.defaultInternalCode();
        if (this.errors.codigo_producto) {
          this.errors = {
            ...this.errors,
            codigo_producto: ''
          };
          delete this.errors.codigo_producto;
        }
      }
    },
    showModal(val) {
      if (val) {
        this.activeSection = 'basico';
        this.saveFeedback = { type: 'info', message: '' };
        if (this.product) {
          // Mapear campos del producto desde ProductosView al formato del modal
          const fallbackCode = this.resolveProductCode(this.product);
          this.productData = {
            producto_id: this.product.id,
            codigo_barras: this.product.codigo_barras || '',
            codigo_producto: this.product.codigo_producto || fallbackCode,
            codigo_plu_balanza: this.product.codigo_plu_balanza || '',
            es_granel: Boolean(this.product.es_granel),
            nombre: this.product.name || '',
            descripcion: this.product.descripcion || '',
            url_imagen: this.product.url_imagen || '',
            categoria_id: this.product.categoria_id || null,
            unidad_medida_id: this.product.unidad_medida_id || null,
            activo: this.product.activo !== undefined ? this.product.activo : true,
            tipo_iva: this.product.tipo_iva || 1
          };
          this.errors = {};
          console.log('[RegisterProductModal] modo edición, datos cargados:', this.productData);
        } else {
          this.resetForm();
          this.barcodeLookupMessage = '';
          this.errors = {};
          console.log('[RegisterProductModal] alta, código precargado =', this.initialCode);
          if (this.initialCode && !this.readOnly) {
            this.lookupBarcodeMetadata();
          }
        }
        this.$nextTick(() => {
          this.focusField('codigo_barras');
        });
      }
    }
  },
  methods: {
    async onCategoriaCreada(categoria) {
      const nuevoId = categoria?.categoria_id ?? categoria?.id ?? null;
      // Recargar primero: si se asigna el id antes de que la opción exista, el
      // <select> no encuentra el value y se queda en blanco.
      await this.$refs.categorySelect?.reload?.();
      if (nuevoId !== null) {
        this.productData.categoria_id = Number(nuevoId);
        delete this.errors.categoria_id;
      }
      this.$nextTick(() => {
        const el = this.$el?.querySelector?.('[data-field="categoria_id"] select');
        el?.focus?.();
      });
    },
    handleViewportChange() {
      if (typeof window === 'undefined') return;
      this.isMobileViewport = window.innerWidth < 992;
    },
    resolveProductCode(product) {
      return String(
        product?.code ||
        product?.codigo_barras ||
        product?.codigo_producto ||
        product?.codigo ||
        ''
      ).trim();
    },
    defaultInternalCode() {
      return String(this.productData.codigo_barras || this.initialCode || '').trim();
    },
    getEffectiveInternalCode() {
      if (this.isEditMode) {
        return String(this.productData.codigo_producto || '').trim();
      }
      return String(this.productData.codigo_producto || '').trim() || this.defaultInternalCode();
    },
    syncDefaultInternalCode(options = {}) {
      const { force = false } = options;
      if (this.isEditMode) return;
      const current = String(this.productData.codigo_producto || '').trim();
      const fallback = this.defaultInternalCode();
      if (fallback && (force || !current || !this.productData.es_granel)) {
        this.productData.codigo_producto = fallback;
      }
    },
    getModalRootElement() {
      const root = this.$refs?.modalRoot;
      if (root instanceof HTMLElement) return root;
      return null;
    },
    focusField(fieldName) {
      const root = this.getModalRootElement();
      if (!root || typeof root.querySelector !== 'function') return;
      const wrapper = root.querySelector(`[data-field="${fieldName}"]`);
      if (!wrapper) return;
      const target = wrapper.querySelector('input, select, textarea, button');
      if (target instanceof HTMLElement && typeof target.focus === 'function') {
        target.focus();
      }
    },
    activateSectionForField(fieldName) {
      if (['descripcion', 'codigo_producto', 'url_imagen', 'activo', 'es_granel', 'codigo_plu_balanza'].includes(fieldName)) {
        this.activeSection = 'avanzado';
        return;
      }
      this.activeSection = 'basico';
    },
    focusFirstError(nextErrors) {
      const firstErrorField = Object.keys(nextErrors)[0];
      if (!firstErrorField) return;
      this.activateSectionForField(firstErrorField);
      this.$nextTick(() => {
        this.focusField(firstErrorField);
      });
    },
    buildSaveErrorMessage(error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detalle ||
        error?.message;
      return backendMessage ? `No se pudo guardar el producto: ${backendMessage}` : 'No se pudo guardar el producto. Verifica los datos e inténtalo de nuevo.';
    },
    validateForm() {
      const nextErrors = {};
      this.syncDefaultInternalCode();
      if (!(this.productData.nombre || '').trim()) nextErrors.nombre = 'El nombre es obligatorio.';
      if (!this.productData.categoria_id) nextErrors.categoria_id = 'Selecciona una categoría.';
      if (!this.productData.unidad_medida_id) nextErrors.unidad_medida_id = 'Selecciona una unidad de medida.';
      this.errors = nextErrors;
      this.saveFeedback = { type: 'info', message: '' };
      if (Object.keys(nextErrors).length > 0) {
        this.focusFirstError(nextErrors);
      }
      return Object.keys(nextErrors).length === 0;
    },
    async lookupBarcodeMetadata() {
      const barcode = (this.productData.codigo_barras || '').trim();
      if (!barcode || this.productData.producto_id) return;

      this.barcodeLookupLoading = true;
      this.barcodeLookupMessage = '';

      try {
        const { data } = await apiService.get(`/api/products/external/barcode/${encodeURIComponent(barcode)}`);
        const nombre = (data?.nombre || '').trim();
        const descripcion = (data?.descripcion || nombre).trim();

        if (!nombre) {
          this.barcodeLookupMessage = 'No se encontraron datos automáticos para este código.';
          return;
        }

        const currentName = (this.productData.nombre || '').trim();
        const currentDescription = (this.productData.descripcion || '').trim();

        if (!currentName || currentName === this.lastAutofillName) {
          this.productData.nombre = nombre;
        }
        if (!currentDescription || currentDescription === this.lastAutofillName) {
          this.productData.descripcion = descripcion;
        }

        this.lastAutofillName = nombre;
        this.barcodeLookupMessage = `Datos sugeridos cargados desde ${data?.fuente || 'catálogo externo'}.`;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.barcodeLookupMessage = 'No se encontraron datos automáticos para este código.';
        } else {
          console.error('Error consultando metadata externa por código de barras:', error);
          this.barcodeLookupMessage = 'No fue posible consultar el catálogo externo.';
        }
      } finally {
        this.barcodeLookupLoading = false;
      }
    },
    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
      });
    },
    async handleImageFile(event) {
      const file = event.target?.files?.[0];
      event.target.value = '';
      if (!file) return;

      this.imageUploadError = '';
      let dataUrl;
      try {
        dataUrl = await this.readFileAsDataUrl(file);
      } catch (error) {
        this.imageUploadError = 'No se pudo leer la imagen seleccionada.';
        return;
      }

      if (this.isEditMode) {
        await this.uploadProductImageNow(dataUrl);
      } else {
        this.pendingImageDataUrl = dataUrl;
        this.pendingImagePreviewUrl = dataUrl;
        this.pendingImageOmitirLimpieza = false;
      }
    },
    async uploadProductImageNow(dataUrl, productoId = this.productData.producto_id, { omitirLimpiezaFondo = false } = {}) {
      if (!productoId) return null;
      this.imageUploadLoading = true;
      this.imageUploadError = '';
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
        const { data } = await apiService.post(`${baseUrl}/${productoId}/image`, {
          data_url: dataUrl,
          omitir_limpieza_fondo: omitirLimpiezaFondo
        });
        this.productData.url_imagen = data.url_imagen;
        this.pendingImageDataUrl = null;
        this.pendingImagePreviewUrl = '';
        this.pendingImageOmitirLimpieza = false;
        return data.url_imagen;
      } catch (error) {
        this.imageUploadError = error?.response?.data?.message || 'No se pudo subir la imagen del producto.';
        return null;
      } finally {
        this.imageUploadLoading = false;
      }
    },
    // Recorte manual (pedido del usuario 2026-08-02): abre la herramienta
    // de recorte sobre un archivo recién elegido, sin subirlo automático.
    async handleImageFileForManualCrop(event) {
      const file = event.target?.files?.[0];
      event.target.value = '';
      if (!file) return;

      this.imageUploadError = '';
      try {
        const dataUrl = await this.readFileAsDataUrl(file);
        this.openCropModal(dataUrl);
      } catch (error) {
        this.imageUploadError = 'No se pudo leer la imagen seleccionada.';
      }
    },
    // Recorta la foto pendiente (tomada con cámara o subida, todavía sin
    // guardar) si hay una, o si no la imagen ya persistida. Las dos fuentes
    // son seguras para el canvas del recortador sin problema de CORS: la
    // pendiente es un data URL (FileReader), la persistida se sirve desde
    // nuestro propio origen (/api/products/images/files/...).
    //
    // Hallazgo de verificación con dispositivo real (UIN-5A-002,
    // SPEC_URL_IMAGEN_NORMALIZACION_v0.1.md): antes este botón sólo
    // aparecía en edición con imagen ya guardada — dar de alta un producto
    // nuevo con foto de cámara no ofrecía ningún recorte con la cámara
    // activada; el único camino pre-guardado era "Recortar manualmente",
    // que reabre el selector de archivo sin `capture`, obligando a buscar
    // la foto recién tomada en la galería del dispositivo.
    openCropModalForCurrentImage() {
      const source = this.pendingImagePreviewUrl || this.productData.url_imagen;
      if (!source) return;
      this.openCropModal(source);
    },
    // Las candidatas de IA son URLs de terceros — cargarlas directo en el
    // canvas del recortador taintearía el export por CORS, así que se
    // descargan primero por el proxy del backend (fetchRemoteImageForCrop).
    async openCropModalForCandidate(candidata) {
      if (!candidata?.url || !this.productData.producto_id) return;
      this.cropCandidateLoading = candidata.url;
      this.iaSearchError = '';
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
        const response = await apiService.get(
          `${baseUrl}/${this.productData.producto_id}/image/fetch-remote`,
          { url: candidata.url },
          { responseType: 'blob' }
        );
        const objectUrl = URL.createObjectURL(response.data);
        this.cropSourceObjectUrl = objectUrl;
        this.openCropModal(objectUrl);
      } catch (error) {
        this.iaSearchError = error?.response?.data?.message || 'No se pudo cargar la candidata para recortar.';
      } finally {
        this.cropCandidateLoading = '';
      }
    },
    openCropModal(src) {
      this.cropImageSrc = src;
      this.showCropModal = true;
    },
    async handleCropConfirmed(dataUrl) {
      this.showCropModal = false;
      this.revokeCropObjectUrl();
      if (this.isEditMode) {
        await this.uploadProductImageNow(dataUrl, undefined, { omitirLimpiezaFondo: true });
      } else {
        this.pendingImageDataUrl = dataUrl;
        this.pendingImagePreviewUrl = dataUrl;
        this.pendingImageOmitirLimpieza = true;
      }
    },
    handleCropCancelled() {
      this.showCropModal = false;
      this.revokeCropObjectUrl();
    },
    revokeCropObjectUrl() {
      if (this.cropSourceObjectUrl) {
        URL.revokeObjectURL(this.cropSourceObjectUrl);
        this.cropSourceObjectUrl = null;
      }
    },
    async searchImageWithIA() {
      if (!this.isEditMode || !this.productData.producto_id) return;
      this.iaSearchLoading = true;
      this.iaSearchError = '';
      this.iaCandidates = null;
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
        const { data } = await apiService.get(`${baseUrl}/${this.productData.producto_id}/image/candidates`);
        this.iaCandidates = Array.isArray(data?.candidatas) ? data.candidatas : [];
      } catch (error) {
        this.iaSearchError = error?.response?.data?.message || 'No se pudieron buscar imágenes con IA.';
      } finally {
        this.iaSearchLoading = false;
      }
    },
    async applyIACandidate(candidata) {
      if (!candidata?.url || !this.productData.producto_id) return;
      this.iaApplyingUrl = candidata.url;
      this.iaSearchError = '';
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
        // sobrescribir: true — la elección explícita de la candidata (con el
        // aviso de reemplazo visible) es la confirmación del operador.
        const { data } = await apiService.post(`${baseUrl}/${this.productData.producto_id}/image/auto`, {
          url: candidata.url,
          fuente: candidata.fuente,
          licencia_confirmada: candidata.licencia_confirmada === true,
          sobrescribir: true
        });
        if (data?.resuelto && data.url_imagen) {
          this.productData.url_imagen = data.url_imagen;
          this.iaCandidates = null;
        } else {
          this.iaSearchError = 'No se pudo aplicar la imagen elegida.';
        }
      } catch (error) {
        this.iaSearchError = error?.response?.data?.message || 'No se pudo aplicar la imagen elegida.';
      } finally {
        this.iaApplyingUrl = '';
      }
    },
    async cleanImageBackground() {
      if (!this.productData.producto_id || !this.productData.url_imagen) return;
      this.cleanBackgroundLoading = true;
      this.cleanBackgroundError = '';
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
        const { data } = await apiService.post(`${baseUrl}/${this.productData.producto_id}/image/clean-background`, {});
        if (data?.url_imagen) {
          this.productData.url_imagen = data.url_imagen;
        } else {
          this.cleanBackgroundError = 'No se pudo limpiar el fondo de la imagen.';
        }
      } catch (error) {
        this.cleanBackgroundError = error?.response?.data?.message || 'No se pudo limpiar el fondo de la imagen.';
      } finally {
        this.cleanBackgroundLoading = false;
      }
    },
    async saveProduct(options = {}) {
      const { resetAfterSave = false } = options;
      if (!this.validateForm()) {
        return;
      }
      this.isSaving = true;
      this.saveFeedback = { type: 'info', message: '' };
      // 1. Determinar si es edición (PUT) o creación (POST)
      const isEdit = !!this.productData.producto_id;
      const baseUrl = process.env.VUE_APP_API_BASE_URL + '/api/products';
      const url     = isEdit
        ? `${baseUrl}/${this.productData.producto_id}`  // PUT sobre /api/products/:id
        : baseUrl;                                      // POST sobre /api/products

      // 2. Ejecutar la llamada correcta
      const payload = {
        ...this.productData,
        codigo_barras: String(this.productData.codigo_barras || '').trim() || null,
        codigo_producto: String(this.getEffectiveInternalCode() || '').trim() || null,
        codigo_plu_balanza: String(this.productData.codigo_plu_balanza || '').trim() || null
      };
      const request = isEdit
        ? apiService.put(url, payload)
        : apiService.post(url, payload);

      // 3. Manejar la respuesta manteniendo tus emits y reseteos
      try {
        const response = await request;
        const payload = response.data;

        // Si había una foto pendiente de subir (alta: no existía producto_id
        // todavía cuando se eligió el archivo), se sube recién ahora con el
        // producto_id real devuelto por la creación.
        if (!isEdit && this.pendingImageDataUrl && payload?.producto_id) {
          const url_imagen = await this.uploadProductImageNow(this.pendingImageDataUrl, payload.producto_id, {
            omitirLimpiezaFondo: this.pendingImageOmitirLimpieza
          });
          if (url_imagen) payload.url_imagen = url_imagen;
        }

        // Emitir igual que antes, con los campos alias
        this.$emit('product-registered', {
          ...payload,
          codigo_barras: payload.codigo_barras,
          codigo:        payload.codigo_barras || payload.codigo_producto
        });
        if (!isEdit && resetAfterSave) {
          this.resetForm();
          this.activeSection = 'basico';
          this.saveFeedback = { type: 'success', message: 'Producto guardado. Puedes cargar otro producto.' };
          this.$nextTick(() => {
            this.focusField('codigo_barras');
          });
          return;
        }
        this.$emit('update:showModal', false);
        this.$emit('close-all-register-modals');
        this.$emit('close');
      } catch (error) {
        console.error("Error guardando producto:", error);
        this.saveFeedback = { type: 'danger', message: this.buildSaveErrorMessage(error) };
      } finally {
        this.isSaving = false;
      }
    },
    async handleSave() {
      await this.saveProduct();
    },
    async handleSaveAndReset() {
      await this.saveProduct({ resetAfterSave: true });
    },
    closeModal(){ 
      if (this.isSaving) return;
      if (this.barcodeLookupTimeoutId) {
        clearTimeout(this.barcodeLookupTimeoutId);
        this.barcodeLookupTimeoutId = null;
      }
      if (!this.isEditMode) {
        this.resetForm();
      }
      this.$emit('update:showModal',false);
      this.$emit('close-all-register-modals');
     },
    handleVolver() {
      this.$router.back();
    },
    resetForm() {
      this.lastAutofillName = '';
      this.barcodeLookupMessage = '';
      this.saveFeedback = { type: 'info', message: '' };
      this.errors = {};
      this.pendingImageDataUrl = null;
      this.pendingImagePreviewUrl = '';
      this.pendingImageOmitirLimpieza = false;
      this.imageUploadError = '';
      this.iaSearchLoading = false;
      this.iaSearchError = '';
      this.iaCandidates = null;
      this.iaApplyingUrl = '';
      this.cleanBackgroundLoading = false;
      this.cleanBackgroundError = '';
      this.showCropModal = false;
      this.revokeCropObjectUrl();
      this.cropImageSrc = '';
      this.cropCandidateLoading = '';
      this.productData = {
        codigo_barras:   this.initialCode,
        codigo_producto: '',
        codigo_plu_balanza: '',
        es_granel: false,
        nombre:          this.initialNombre,
        descripcion:     '',
        url_imagen:      '',
        categoria_id:    null,
        unidad_medida_id:null,
        activo:          true,
        tipo_iva:        this.initialTipoIva || 1
      };
      this.syncDefaultInternalCode({ force: true });
    }
  },
  mounted() {
    this.handleViewportChange();
    window.addEventListener('resize', this.handleViewportChange);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportChange);
    if (this.barcodeLookupTimeoutId) {
      clearTimeout(this.barcodeLookupTimeoutId);
      this.barcodeLookupTimeoutId = null;
    }
  }
};
</script>

<style scoped>
.product-image-preview {
  width: 88px;
  height: 88px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.5rem;
}

.product-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}

.product-image-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.ia-candidates {
  margin-top: 0.5rem;
}

.ia-candidates-grid {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.ia-candidate {
  width: 88px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
}

.ia-candidate:hover:not(:disabled) {
  border-color: #0d6efd;
}

.ia-candidate--applying {
  opacity: 0.6;
}

.ia-candidate-apply {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.ia-candidate img {
  width: 100%;
  height: 72px;
  object-fit: contain;
  background: #f8f9fa;
}

.ia-candidate-fuente {
  font-size: 0.65rem;
  text-align: center;
  padding: 0.1rem 0.2rem;
  color: #6c757d;
  border-top: 1px solid #e9ecef;
}

.ia-candidate-crop {
  margin: 0 0.25rem 0.25rem;
  font-size: 0.65rem;
  padding: 0.1rem 0.25rem;
}

.register-product-modal .modal-dialog {
  max-width: 920px;
}

.register-product-modal {
  z-index: 1080;
}

.register-product-backdrop {
  z-index: 1070;
}

/* Convención de capas para modales apilados (SPEC catalogo-alta-unica-v1 §7.2).
   `AppModal` no declara z-index propio: hereda los de Bootstrap 5.3
   (modal 1055, backdrop 1050), que quedan POR DEBAJO del backdrop de este
   modal (1070). Sin esta elevación, el modal de categoría se abre detrás de
   un backdrop opaco: se ve como si el botón `+` no hiciera nada.

     modal base (este)      1080   |   su backdrop          1070
     modal apilado encima   1090   |   su backdrop          1085

   La elevación se declara desde quien apila, no en `AppModal`: subirla allá
   la subiría para todos los modales de la app, incluidos los que hoy conviven
   bien. El apilado es una propiedad de la relación entre dos modales. */
.stacked-modal-layer :deep(.modal) {
  z-index: 1090;
}

.stacked-modal-layer :deep(.modal-backdrop) {
  z-index: 1085;
}

.modal-content {
  border-radius: 18px;
  border: none;
  box-shadow: 0 28px 48px -20px rgba(15, 23, 42, 0.35);
  overflow: hidden;
}

.modal-header {
  padding: 24px 28px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(14, 116, 144, 0.08));
}

.modal-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-eyebrow {
  margin: 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #0f172a;
  opacity: 0.6;
  font-weight: 600;
}

.modal-title {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
}

.btn-close {
  font-size: 1.125rem;
  color: #0f172a;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 28px;
  background: #f8fafc;
}

.mobile-section-toggle {
  display: none;
  gap: 8px;
  margin-bottom: 14px;
}

.btn-section {
  flex: 1;
  border-radius: 999px;
  background: #e2e8f0;
  color: #0f172a;
  font-weight: 700;
  padding: 10px 14px;
}

.btn-section.active {
  background: linear-gradient(120deg, #2563eb, #0ea5e9);
  color: #fff;
}

.form-wrapper {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.save-feedback {
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
}

.save-feedback--success {
  background: rgba(22, 163, 74, 0.12);
  color: #166534;
}

.save-feedback--danger {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}

.product-quick-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.summary-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(226, 232, 240, 0.95);
}

.summary-item strong {
  overflow-wrap: anywhere;
  word-break: break-word;
  color: #0f172a;
}

.summary-label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.product-description-summary {
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff7ed;
  border: 1px solid rgba(251, 146, 60, 0.22);
}

.product-description-summary p {
  margin: 6px 0 0;
  color: #431407;
  line-height: 1.45;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px 24px;
}

.field-hint {
  display: block;
  margin-top: 6px;
  font-size: 0.8rem;
  color: #475569;
}

.field-error {
  display: block;
  margin-top: 6px;
  font-size: 0.8rem;
  color: #dc2626;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-block + .section-block {
  margin-top: 26px;
  padding-top: 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.section-heading {
  margin-bottom: 16px;
}

.section-title {
  margin: 0 0 4px;
  font-weight: 700;
  color: #0f172a;
}

.section-copy {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

.form-label {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.form-control,
.form-select {
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  padding: 10px 12px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.form-control.is-invalid,
.form-select.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.switch-field {
  align-self: center;
}

.form-switcher {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(226, 232, 240, 0.6);
  border-radius: 999px;
  padding: 6px 14px;
}

.form-check-input {
  width: 46px;
  height: 24px;
  margin: 0;
  cursor: pointer;
}

.form-check-label {
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 28px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  background: #fff;
}

.modal-hint {
  font-size: 0.85rem;
  color: #475569;
  max-width: 60%;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn {
  border-radius: 999px;
  padding: 10px 22px;
  font-weight: 600;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.btn-secondary:hover {
  background: #cbd5f5;
}

.btn-primary {
  background: linear-gradient(120deg, #2563eb, #0ea5e9);
  box-shadow: 0 14px 24px -14px rgba(37, 99, 235, 0.6);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px -14px rgba(37, 99, 235, 0.75);
}

.btn-outline-primary {
  background: #fff;
  color: #2563eb;
  border: 1px solid rgba(37, 99, 235, 0.22);
}

@media (max-width: 991.98px) {
  .register-product-modal {
    padding-top: env(safe-area-inset-top, 0);
  }

  .register-product-modal .modal-dialog {
    max-width: 100%;
    margin: 0;
    min-height: 100vh;
  }

  .modal-content {
    border-radius: 0;
    min-height: 100vh;
  }

  .modal-body {
    padding: 16px 16px 120px;
  }

  .form-wrapper {
    padding: 16px;
  }

  .product-quick-summary {
    grid-template-columns: 1fr;
  }

  .mobile-section-toggle {
    display: flex;
  }

  .section-hidden-mobile {
    display: none;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modal-footer {
    position: sticky;
    bottom: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
    box-shadow: 0 -12px 24px -24px rgba(15, 23, 42, 0.35);
  }

  .modal-actions {
    width: 100%;
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }

  .modal-hint {
    max-width: 100%;
  }
}

.category-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-field-row :deep(select) {
  flex: 1 1 auto;
  min-width: 0;
}

.category-add-btn {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  line-height: 1;
  font-weight: 700;
  padding: 0;
}
</style>
